import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import UsersFilters from './UsersFilters';
import { EMPTY_FILTERS } from '../urlState';

const ACCOUNTS = [
  { label: 'Account Name A', value: 1 },
  { label: 'Account Name B', value: 2 },
];
const PERMISSIONS = [
  { label: 'Fleet Administrator', value: 'Fleet Administrator' },
  { label: 'Member', value: 'Member' },
];
const JOB_ROLES = [
  { label: 'Fleet Manager', value: 'fleet_manager' },
  { label: 'Technician', value: 'technician' },
];

const renderFilters = (props = {}) => {
  const onChange = jest.fn();
  const onSearchAccounts = jest.fn();

  render(
    <ConfigProvider>
      <UsersFilters
        accountOptions={ACCOUNTS}
        filters={EMPTY_FILTERS}
        jobRoleOptions={JOB_ROLES}
        permissionOptions={PERMISSIONS}
        onChange={onChange}
        onSearchAccounts={onSearchAccounts}
        {...props}
      />
    </ConfigProvider>,
  );

  return { onChange, onSearchAccounts };
};

const openMenu = (name) =>
  userEvent.click(screen.getByRole('button', { name: new RegExp(name, 'i') }));

describe('management/Users UsersFilters', () => {
  it('offers the three filters, without Teams', () => {
    renderFilters();

    expect(screen.getByText('All Accounts')).toBeInTheDocument();
    expect(screen.getByText('All Permissions')).toBeInTheDocument();
    expect(screen.getByText('All Job Roles')).toBeInTheDocument();
    // Teams wait on PD-4213.
    expect(screen.queryByText('All Teams')).not.toBeInTheDocument();
  });

  describe('applying a selection', () => {
    // The footer is the whole point of the staged draft: checking a box must not
    // refetch on its own.
    it('reports nothing until Apply is clicked', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByText('Member'));

      expect(onChange).not.toHaveBeenCalled();
    });

    it('reports the checked values on Apply', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByText('Member'));
      fireEvent.click(screen.getByRole('button', { name: /apply/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: ['Member'],
        }),
      );
    });

    it('empties the filter on Clear', async () => {
      const { onChange } = renderFilters({
        filters: { ...EMPTY_FILTERS, permissions: ['Member'] },
      });

      openMenu('Permissions');
      await waitFor(() =>
        expect(
          screen.getByRole('button', { name: /clear$/i }),
        ).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByRole('button', { name: /clear$/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: [],
        }),
      );
    });
  });

  // Only Permissions gets this row.
  describe('the All row', () => {
    it('checks every permission at once', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() => expect(screen.getByText('All')).toBeInTheDocument());

      fireEvent.click(screen.getByText('All'));
      fireEvent.click(screen.getByRole('button', { name: /apply/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: ['Fleet Administrator', 'Member'],
        }),
      );
    });

    it('is absent from the other menus', async () => {
      renderFilters();

      openMenu('All Job Roles');
      await waitFor(() =>
        expect(screen.getByText('Technician')).toBeInTheDocument(),
      );

      expect(screen.queryByText('All')).not.toBeInTheDocument();
    });
  });

  describe('searching', () => {
    // Accounts are global, so the server narrows them.
    it('asks the server when searching accounts', async () => {
      const { onSearchAccounts } = renderFilters();

      openMenu('All Accounts');
      await waitFor(() =>
        expect(
          screen.getByPlaceholderText('Search accounts'),
        ).toBeInTheDocument(),
      );

      fireEvent.change(screen.getByPlaceholderText('Search accounts'), {
        target: { value: 'name b' },
      });

      await waitFor(() => expect(onSearchAccounts).toHaveBeenCalled());
    });

    // Job roles are a bounded list, so they narrow in the browser.
    it('narrows job roles without a request', async () => {
      renderFilters();

      openMenu('All Job Roles');
      await waitFor(() =>
        expect(screen.getByText('Technician')).toBeInTheDocument(),
      );

      fireEvent.change(screen.getByPlaceholderText('Search job roles'), {
        target: { value: 'tech' },
      });

      await waitFor(() =>
        expect(screen.queryByText('Fleet Manager')).not.toBeInTheDocument(),
      );
      expect(screen.getByText('Technician')).toBeInTheDocument();
    });

    it('gives Permissions no search input', async () => {
      renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      expect(screen.queryByPlaceholderText(/search/i)).not.toBeInTheDocument();
    });
  });

  describe('Clear All', () => {
    it('is hidden while nothing is filtered', () => {
      renderFilters();

      expect(
        screen.queryByRole('button', { name: /clear all/i }),
      ).not.toBeInTheDocument();
    });

    it('appears once a filter is active and empties them all', () => {
      const { onChange } = renderFilters({
        filters: { ...EMPTY_FILTERS, jobRoles: ['technician'] },
      });

      userEvent.click(screen.getByRole('button', { name: /clear all/i }));

      expect(onChange).toHaveBeenCalledWith(EMPTY_FILTERS);
    });
  });
});
